import { useState } from "react";

import type { BudgetLineRow, ProjectOwnerWithUser } from "@/hooks/use-project-detail";
import type { UserLookup } from "@/hooks/use-users";
import type { LoggedEntryRef } from "@/hooks/use-budget-line-logged-hours";
import { cn } from "@/lib/utils";
import { AssignmentEditor } from "./AssignmentEditor";
import { SrIcon } from "@/components/ui/SrIcon";
import { TaskComments } from "./TaskComments";
import { useTaskCommentCounts } from "@/hooks/use-task-comments";
import SplitBudgetLineDialog from "./SplitBudgetLineDialog";
import { ConvertTaskToCostButton } from "./ConvertTaskToCostButton";
import { BudgetFordelingPill } from "./BudgetFordelingPill";
import { LooseEntriesPanel, summarizeLoose } from "./LooseEntriesPanel";
import { BudgetAssignedToCell } from "./BudgetAssignedToCell";
import { BudgetHoursBar } from "./BudgetHoursBar";
import { Avatar } from "@/components/shared/Avatar";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { statusFor, STATUS_TEXT } from "@/lib/budget-status";
import { BudgetAssignmentRow } from "./BudgetAssignmentRow";

/**
 * Helper function to calculate allocation status (used by footer/legacy)
 */
export function getAllocationStatus(
  budgetHours: number,
  assignedHours: number,
): { label: string; bg: string; text: string } | null {
  if (budgetHours <= 0) return null;
  if (assignedHours >= budgetHours)
    return { label: "Fordelt", bg: "bg-[#D9F5E8]", text: "text-[#035C39]" };
  if (assignedHours > 0)
    return { label: "Delvis fordelt", bg: "bg-[#FEF3C7]", text: "text-[#78350F]" };
  return { label: "Ikke fordelt", bg: "bg-[#FFE1DB]", text: "text-[#7F1D1D]" };
}

/**
 * Phase order for sorting in TimerTab
 */
export const PHASE_ORDER: Record<string, number> = {
  Pre: 0,
  Preprod: 0,
  Manus: 0,
  Prod: 1,
  Produksjon: 1,
  Post: 2,
  Postprod: 2,
  Postproduksjon: 2,
  Reise: 3,
  Annet: 4,
};

/**
 * Simple display-only info row (used by other tabs)
 */
export function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value ?? "—"}</span>
    </div>
  );
}

interface BudgetLineRowProps {
  bl: BudgetLineRow;
  projectId: string;
  users: UserLookup[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  readOnly?: boolean;
  loggedHours?: number;
  loggedHoursPerUser?: Map<string, number>;
  loggedHoursPerAssignment?: Map<string, number>;
  /** VIB-1201: user_id → underliggende føringer, for hover-tooltip på «Brukt». */
  entriesPerUser?: Map<string, LoggedEntryRef[]>;
  canAssignHours?: boolean;
  /** Eierdata — ikke brukt p.t., men beholdes for kompat. med kallesteder. */
  owners?: ProjectOwnerWithUser[];
}

/**
 * Budget line row — matcher V3-designet (variant3.jsx). 6 kolonner:
 * disclosure / oppgave / tildelt-til / Timer / Brukt / Forbruk.
 */
export function BudgetLineRowComponent({
  bl,
  projectId,
  users,
  editingId,
  setEditingId,
  readOnly = false,
  loggedHours = 0,
  loggedHoursPerUser,
  loggedHoursPerAssignment,
  entriesPerUser,
  canAssignHours = false,
}: BudgetLineRowProps) {
  const isEditing = editingId === bl.id;
  const assignedHours = bl.assignments.reduce((s, a) => s + a.hours, 0);
  const budgetHrs = bl.quantity_hours ?? 0;
  const roundedLogged = Math.round(loggedHours * 10) / 10;
  const taskStatus = statusFor(budgetHrs, loggedHours);
  const hasLabeledSubTasks =
    bl.assignments.some((a) => a.label && a.label.trim().length > 0);
  const [expanded, setExpanded] = useState(hasLabeledSubTasks && bl.assignments.length >= 2);
  const [openCommentAssignmentId, setOpenCommentAssignmentId] = useState<string | null>(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const assignmentIds = bl.assignments.map((a) => a.id);
  const { data: commentCounts } = useTaskCommentCounts(expanded ? assignmentIds : []);

  // VIB-545: Løse timeføringer (timer ført uten tildeling) for kombinert pille
  const { timeEntries, project, isAdmin, activeUserId } = useProjectDetailContext();

  const looseSummary = bl.cost_type === "internal"
    ? summarizeLoose(bl, timeEntries)
    : { hours: 0, count: 0 };

  // Sum kommentarer på tvers av alle assignments — brukes for inline-indikator
  // når raden er kollapset, så PL ser at det finnes diskusjon uten å ekspandere.
  const totalCommentCount = (commentCounts
    ? Array.from(commentCounts.values()).reduce((s, n) => s + n, 0)
    : 0);

  const isLocked = bl.is_locked === true;
  const canEditAssignment = !readOnly && canAssignHours && !isLocked;

  return (
    <>
      <tr className="border-b border-border/40 hover:bg-muted/20 transition-colors group">
        <td className="px-2 py-3 align-top">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-muted-foreground hover:text-primary transition-colors"
            aria-expanded={expanded}
            title={expanded ? "Skjul tildeling" : "Vis tildeling"}
          >
            <SrIcon
              name="chevron-right"
              className={cn("w-4 h-4 transition-transform", expanded && "rotate-90")}
            />
          </button>
        </td>

        <td className="px-2 py-3 align-top">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              onClick={() => setExpanded(!expanded)}
              className="font-medium cursor-pointer hover:text-primary"
            >
              {bl.description}
            </span>
            {isLocked && (
              <SrIcon
                name="lock"
                className="w-3 h-3 text-muted-foreground"
                title="Låst — tilbudsskriving auto-tildelt"
              />
            )}

            {budgetHrs > 0 && (
              <BudgetFordelingPill
                budgetHours={budgetHrs}
                assignedHours={assignedHours}
                looseEntryHours={looseSummary.hours}
                looseEntryCount={looseSummary.count}
              />
            )}

            {/* Inline kommentar-indikator når raden er kollapset (VIB I) */}
            {!expanded && totalCommentCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <SrIcon name="message-square" className="w-3 h-3" />
                {totalCommentCount} kommentar{totalCommentCount === 1 ? "" : "er"}
              </span>
            )}

            {!isEditing && canEditAssignment && bl.assignments.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(bl.id);
                }}
                className="ml-auto opacity-0 xl:group-hover:opacity-100 xl:focus-visible:opacity-100 xl:group-focus-within:opacity-100 transition-opacity text-muted-foreground hover:text-primary p-1.5"
                title="Rediger tildeling"
              >
                <SrIcon name="pencil" className="w-3 h-3" />
              </button>
            )}
          </div>
        </td>

        <td className="px-2 py-3 align-middle">
          <BudgetAssignedToCell
            budgetLine={bl}
            onAssign={() => canEditAssignment && setEditingId(bl.id)}
            disabled={!canEditAssignment}
            hideAssignButton={expanded}
          />
        </td>

        <td className="px-2 py-3 text-right tabular-nums align-top font-medium">
          {budgetHrs > 0 ? `${budgetHrs}t` : "—"}
        </td>

        <td
          className={cn(
            "px-2 py-3 text-right tabular-nums align-top font-semibold",
            STATUS_TEXT[taskStatus],
          )}
        >
          {roundedLogged > 0 ? `${roundedLogged}t` : "0t"}
        </td>

        <td className="px-2 py-3 align-middle">
          {budgetHrs > 0 ? (
            <BudgetHoursBar assigned={budgetHrs} used={loggedHours} height={4} />
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      </tr>

      {/* Ekspansjon: assignments-grid, action-knapper, admin-verktøy */}
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-muted/15 border-b border-border/40 px-0 py-0">
            <div className="px-6 py-4 space-y-3">
              {/* Løse timeføringer (rød panel) — alltid synlig, også i edit-modus */}
              <LooseEntriesPanel budgetLine={bl} projectId={projectId} />

              {!isEditing && (<>
              {bl.assignments.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  Ingen tildelinger ennå.
                </p>
              ) : (
                <>
                  {bl.assignments.map((a) => {
                    const userLogged =
                      loggedHoursPerAssignment?.get(a.id) ??
                      loggedHoursPerUser?.get(a.user_id) ??
                      0;
                    const commentsOpen = openCommentAssignmentId === a.id;

                    return (
                      <BudgetAssignmentRow
                        key={a.id}
                        assignment={a}
                        loggedHours={userLogged}
                        entries={entriesPerUser?.get(a.user_id)}
                        commentCount={commentCounts?.get(a.id) ?? 0}
                        commentsOpen={commentsOpen}
                        onToggleComments={() =>
                          setOpenCommentAssignmentId(commentsOpen ? null : a.id)
                        }
                        readOnly={readOnly}
                      />
                    );
                  })}
                </>
              )}

              {/* Action-knapper: Tildel person, Splitt, Konverter til kostnad, Kommenter */}
              {!readOnly && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {canEditAssignment && (
                    <button
                      onClick={() => setEditingId(bl.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary text-primary rounded hover:bg-primary/5 transition-colors"
                    >
                      <SrIcon name="plus" className="w-3 h-3" />
                      Tildel person
                    </button>
                  )}
                  {canEditAssignment && bl.cost_type === "internal" && budgetHrs > 0 && (
                    <button
                      onClick={() => setSplitOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border text-muted-foreground rounded hover:bg-muted transition-colors"
                    >
                      Splitt
                    </button>
                  )}
                  {canEditAssignment && bl.cost_type === "internal" && budgetHrs > 0 && (
                    <ConvertTaskToCostButton bl={bl} projectId={projectId} />
                  )}
                </div>
              )}
              </>)}

            </div>
          </td>
        </tr>
      )}

      {isEditing && (
        <AssignmentEditor
          budgetLine={bl}
          projectId={projectId}
          users={users}
          onClose={() => setEditingId(null)}
          canAssignHours={canAssignHours}
        />
      )}

      <SplitBudgetLineDialog
        budgetLine={splitOpen ? bl : null}
        projectId={projectId}
        open={splitOpen}
        onOpenChange={setSplitOpen}
      />
    </>
  );
}

